import React, { useEffect, useState } from "react";
import { useTheme } from "@mui/material";
import PanelistProfileFields from "../modals/PanelistProfileFields";
import { ErrorBox, Notice, PrimaryButton, Section, Spinner, TextButton } from "../organisation/OrgModal";
import { validateLastName, validateName } from "../auth/validationHelpers";
import { cachedActiveOrganisation, fetchMe, interpretProfileError, ORG_ROLE, orgErrorMessage, updateMyProfile } from "../../utils/organisationApi";
import { emptyPanelistProfile, panelistFieldError, profileFromPanelist, selfPanelistPayload } from "../../utils/panelists";

const formFromMe = (me) => ({
  firstName: me.user.firstName,
  lastName: me.user.lastName,
  ...(me.panelist ? profileFromPanelist(me.panelist) : emptyPanelistProfile()),
});

// What a save would send, so unchanged forms keep Save disabled
const savedShape = (form, withPanelist) =>
  JSON.stringify({
    firstName: form.firstName.trim(),
    lastName: form.lastName.trim(),
    panelist: withPanelist ? selfPanelistPayload(form) : null,
  });

// Profile tab: one form, one save. Anyone with a panelist profile in this organisation edits it here too.
const ProfileSettings = () => {
  const theme = useTheme();
  const organisationName = cachedActiveOrganisation()?.name || "your organisation";

  const [email, setEmail] = useState("");
  const [isPanelist, setIsPanelist] = useState(false);
  const [hasPanelistRecord, setHasPanelistRecord] = useState(false);
  const [saved, setSaved] = useState(null);
  const [form, setForm] = useState(null);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [notice, setNotice] = useState("");

  useEffect(() => {
    let cancelled = false;
    fetchMe()
      .then((me) => {
        if (cancelled) return;
        const initial = formFromMe(me);
        setEmail(me.user.email);
        setIsPanelist(me.role === ORG_ROLE.PANELIST);
        setHasPanelistRecord(Boolean(me.panelist));
        setSaved(initial);
        setForm(initial);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(orgErrorMessage(err, "Could not load your profile."));
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Designation, experience and skills are for Panelists only. GET /me returns panelist when this person's email
  // matches a panelist in the organisation, but an HR or Admin with such a record doesn't edit it here.
  const showPanelistFields = isPanelist && hasPanelistRecord;
  const isDirty = Boolean(form && saved) && savedShape(form, showPanelistFields) !== savedShape(saved, showPanelistFields);

  const update = (field) => (value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "", panelist: "", api: "" }));
    setNotice("");
  };

  const handleCancel = () => {
    setForm(saved);
    setErrors({});
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const firstName = form.firstName.trim();
    const lastName = form.lastName.trim();
    const nextErrors = {
      firstName: validateName(firstName),
      lastName: validateLastName(lastName),
      panelist: showPanelistFields ? panelistFieldError(form) : "",
    };
    setErrors(nextErrors);
    if (Object.values(nextErrors).some(Boolean)) return;

    setIsSaving(true);
    try {
      const me = await updateMyProfile({
        firstName,
        lastName,
        panelist: showPanelistFields ? selfPanelistPayload(form) : undefined,
      });
      // The response has the same shape as GET /me, so the form starts again from it
      const next = formFromMe(me);
      setHasPanelistRecord(Boolean(me.panelist));
      setSaved(next);
      setForm(next);
      setNotice("Profile saved.");
    } catch (err) {
      const failure = interpretProfileError(err);
      setErrors(failure.field ? { [failure.field]: failure.message } : { api: failure.message });
    } finally {
      setIsSaving(false);
    }
  };

  const inputClass =
    "w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-accent-gray-light";
  const inputStyle = { borderColor: theme.palette.secondary.v4, color: theme.palette.text.main };
  const labelClass = "block text-sm font-medium mb-2";

  return (
    <div
      className="flex flex-col w-full h-full overflow-auto"
      style={{ backgroundColor: theme.palette.primary.v2, color: theme.palette.text.main }}
    >
      <Notice onDismiss={() => setNotice("")}>{notice}</Notice>

      {isLoading ? (
        <Spinner label="Loading profile..." />
      ) : loadError ? (
        <div className="m-6">
          <ErrorBox>{loadError}</ErrorBox>
        </div>
      ) : (
        <div className="p-6 max-md:p-4 w-full max-w-3xl">
          <Section>
            <form onSubmit={handleSave} className="flex flex-col gap-5" noValidate>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="profile-first-name" className={labelClass}>
                    First name <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="profile-first-name"
                    type="text"
                    value={form.firstName}
                    onChange={(e) => update("firstName")(e.target.value)}
                    maxLength={100}
                    autoComplete="given-name"
                    className={inputClass}
                    style={inputStyle}
                  />
                  {errors.firstName && <p className="text-accent-red text-sm mt-1">{errors.firstName}</p>}
                </div>
                <div>
                  <label htmlFor="profile-last-name" className={labelClass}>
                    Last name <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="profile-last-name"
                    type="text"
                    value={form.lastName}
                    onChange={(e) => update("lastName")(e.target.value)}
                    maxLength={100}
                    autoComplete="family-name"
                    className={inputClass}
                    style={inputStyle}
                  />
                  {errors.lastName && <p className="text-accent-red text-sm mt-1">{errors.lastName}</p>}
                </div>
              </div>

              <div>
                <label htmlFor="profile-email" className={labelClass}>
                  Email
                </label>
                {/* Greyed out: the email is the login and can't be edited */}
                <input
                  id="profile-email"
                  type="email"
                  value={email}
                  disabled
                  readOnly
                  aria-describedby="profile-email-hint"
                  className="w-full px-3 py-2 border rounded-md bg-gray-100 cursor-not-allowed"
                  style={{ borderColor: theme.palette.secondary.v4, color: "#6b7280" }}
                />
              </div>

              {showPanelistFields && (
                <div>
                  <PanelistProfileFields
                    idPrefix="profile-panelist"
                    role={form.role}
                    onRoleChange={update("role")}
                    experience={form.experience}
                    onExperienceChange={update("experience")}
                    skills={form.skills}
                    onSkillsChange={update("skills")}
                    skillDraft={form.skillDraft}
                    onSkillDraftChange={update("skillDraft")}
                    inputClassName={inputClass}
                    inputStyle={inputStyle}
                  />
                  <p className="text-xs text-accent-gray-light mt-2">
                    HR uses your designation, experience and skills to pick panelists for interviews at {organisationName}.
                  </p>
                  {errors.panelist && <p className="text-accent-red text-sm mt-1">{errors.panelist}</p>}
                </div>
              )}

              {isPanelist && !hasPanelistRecord && (
                <p className="text-sm text-accent-gray-light">
                  Your panelist details aren't set up yet. Ask HR at {organisationName} to add you as a panelist.
                </p>
              )}

              <ErrorBox>{errors.api}</ErrorBox>

              <div className="flex justify-end gap-3">
                <TextButton onClick={handleCancel} disabled={!isDirty || isSaving}>
                  Cancel
                </TextButton>
                <PrimaryButton type="submit" disabled={!isDirty || isSaving}>
                  {isSaving ? "Saving..." : "Save changes"}
                </PrimaryButton>
              </div>
            </form>
          </Section>
        </div>
      )}
    </div>
  );
};

export default ProfileSettings;
